/**
 * Initializes header labels for a segmentation table based on an array of column names.
 * The function determines `header1` as the column containing "customer" (case-insensitive),
 * defaulting to the first column if no match is found. `header2` is set to a column
 * containing "prospect" (case-insensitive) if available and different from `header1`,
 * otherwise it defaults to an empty string.
 *
 * @param {string[]} columns - An array of column names to analyze for header initialization.
 * @returns {[string, string]} A tuple containing [header1, header2] where each header is
 * prefixed with "# " followed by the selected column name, or an empty string if
 * no suitable column is found.
 *
 * @example
 * // Example 1: Columns with "customer" and "prospect"
 * initializeHeaders(["CustomerCount", "ProspectData", "Sales"]);
 * // Returns: ["# CustomerCount", "# ProspectData"]
 *
 * @example
 * // Example 2: No "customer" or "prospect"
 * initializeHeaders(["Sales", "Revenue"]);
 * // Returns: ["# Sales", ""]
 *
 * @example
 * // Example 3: Empty array
 * initializeHeaders([]);
 * // Returns: ["", ""]
 */
export const initializeHeaders = (columns: string[]): [string, string] => {
  const customerColumn = columns.find((col) => /customer/i.test(col));
  const header1 = customerColumn ? `# ${customerColumn}` : columns.length > 0 ? `# ${columns[0]}` : "";

  const prospectColumn = columns.find((col) => /prospect/i.test(col) && col !== customerColumn);
  const header2 = prospectColumn ? `# ${prospectColumn}` : "";
  return [header1, header2];
};
